import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router';

import { AuthProvider } from './contexts/AuthContext';
import { AppRoutes, ROUTES } from './routes';
import {
  clearAdaptiveTestFromLocalStorage,
  consumeAdaptiveTestStorageReset,
  saveAdaptiveTestToLocalStorage,
  shouldFetchNewAdaptiveTest,
} from './services/adaptiveVocabTestStorage';
import { useCreateAdaptiveVocabTest, useUpdateAdaptiveProfile } from './services/l2ApiAdaptive';
import { getUserProfileFromLocalStorage } from './services/userProfile';
import { clearVocabResultData } from './services/vocabResultData';
import { generateTestVersionString } from './utils/featureFlagVersion';
import { setupVWOErrorHandler } from './utils/vwoErrorHandler';

setupVWOErrorHandler();

function AppContent() {
  const basename = import.meta.env.BASE_URL.replace(/\/$/, '');
  return (
    <BrowserRouter basename={basename}>
      <AuthProvider>
        <CoreRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export const App = Sentry.withErrorBoundary(AppContent, {
  fallback: <div>Something went wrong</div>,
});

function CoreRoutes() {
  const location = useLocation();
  const { isPending, mutate } = useCreateAdaptiveVocabTest();
  const { mutate: mutateProfile } = useUpdateAdaptiveProfile();
  const navigate = useNavigate();

  useEffect(() => {
    console.debug('Path changed:', window.location.pathname);

    const shouldFetchNewTest = shouldFetchNewAdaptiveTest();
    if (consumeAdaptiveTestStorageReset()) {
      clearVocabResultData();
      void navigate(ROUTES.HOME, { replace: true });
      return;
    }

    if (shouldFetchNewTest && !isPending) {
      console.log('[App] Should fetch new adaptive test');
      clearAdaptiveTestFromLocalStorage();
      clearVocabResultData();

      const webTestVersion = generateTestVersionString();
      mutate(
        {
          web_test_version: webTestVersion,
          language: 'en',
          test_mode: 'batch_words',
          // Placeholder — backend ignores this for batch_words mode and
          // overrides with the server-owned PAGINATED_QUESTIONS_IN_PAGE.
          // Must be a positive integer to pass backend validation.
          client_buffer_size: 1,
        },
        {
          onSuccess: (test) => {
            console.log('[App] Created new adaptive vocab test:', test);
            saveAdaptiveTestToLocalStorage(test);
            console.debug('Created new adaptive vocab test');

            // Carry the previously collected profile over to the new test
            // (retry after a discarded test, or a returning user)
            const storedProfile = getUserProfileFromLocalStorage();
            if (storedProfile.native_language) {
              mutateProfile(
                { testId: test.test_id, profile: storedProfile },
                {
                  onError: (error) => {
                    // Best-effort carry-over: log but don't block the test. The profile
                    // was already saved on a prior test, so the server can recover it from
                    // any of the user's tests — a failure here is non-fatal.
                    console.warn('[App] Failed to carry profile over to new test:', error);
                  },
                },
              );
            }
          },
          onError: (error) => {
            console.error('[App] Error creating new adaptive test:', error);
            void navigate(ROUTES.ERROR);
          },
        },
      );
    }
  }, [location.pathname, isPending, mutate, mutateProfile, navigate]);

  return <AppRoutes />;
}
